import { useMemo } from 'react'
import RenderHTML from '../ui-components/RenderHTML'
import { RollingAnimation } from '../ui-components/RollingAnimation'

type PaymentMethodsButtonProps = {
  iconSVG?: string
  iconUrl?: string
  iconClassName?: string
  onMiddleTextsClick: () => void
  middleTexts: {
    type: 'NORMAL' | 'WITH_DOWN_CARAT' | 'NORMAL_BOLD'
    text: string
  }[]
  onCTAClick: () => void
  ctaLabel: string
  isDisabled: boolean
}

export const PaymentMethodsButton = ({
  iconSVG,
  iconUrl,
  iconClassName,
  middleTexts,
  onMiddleTextsClick,
  onCTAClick,
  ctaLabel,
  isDisabled
}: PaymentMethodsButtonProps) => {
  // Calculate the value to track for rolling animation
  const rollingContent = useMemo(
    () => ({ middleTexts, iconSVG, iconUrl, iconClassName }),
    [middleTexts, iconSVG, iconUrl, iconClassName]
  )

  return (
    <div
      test-locator='MFE_SELECT_AMOUNT_CTA_SELECT_BANK'
      className='flex items-center bg-white w-full animate-fade-250'
    >
      <div
        onClick={onMiddleTextsClick}
        className='flex items-center w-1 flex-1 border border-[#F0F4F7] rounded-[16px] rounded-r-none relative overflow-hidden'
      >
        <RollingAnimation value={rollingContent} className='w-full h-full'>
          <RollingContentWrapper />
        </RollingAnimation>
      </div>

      <div
        test-locator='MFE_SELECT_AMOUNT_CTA_PAY_NOW'
        onClick={isDisabled ? undefined : onCTAClick}
        className={`self-stretch flex items-center justify-center
          ${isDisabled ? 'bg-white text-[#D3D3D3] transition-none' : 'text-[#095EB2] bg-[#E6F2FF] transition-colors duration-300 delay-75'}
          font-[500] text-[16px] w-[109px] border border-[#F0F4F7] rounded-[16px] -ml-[2px] rounded-l-none`}
      >
        {ctaLabel}
      </div>
    </div>
  )
}

const downCaratIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="9" height="7" viewBox="0 0 9 7" fill="none">
  <path d="M8.74781 1.63672C8.7503 1.73869 8.73286 1.8402 8.69703 1.93555C8.66112 2.03104 8.607 2.11891 8.53785 2.19336L5.22535 5.75781L5.13746 5.67676L5.22438 5.75879C5.11435 5.87368 4.98279 5.96543 4.83766 6.02832C4.69255 6.09118 4.53642 6.12409 4.37867 6.125C4.22095 6.12588 4.06448 6.09441 3.91871 6.0332C3.77301 5.97198 3.64027 5.88211 3.52906 5.76855L3.52711 5.7666L0.205822 2.1875C0.0670204 2.03786 -0.00738243 1.83792 -0.00218585 1.63281C0.00305269 1.42783 0.0879898 1.23293 0.234142 1.09082C0.380531 0.948557 0.577029 0.870587 0.780041 0.875976C0.982884 0.881433 1.17442 0.969552 1.31324 1.11914L4.30738 4.3457C4.31562 4.3546 4.3261 4.36252 4.33668 4.36719C4.34716 4.37174 4.35869 4.37402 4.36988 4.37402C4.38113 4.37398 4.39258 4.37182 4.40309 4.36719C4.41357 4.36255 4.42323 4.35547 4.43141 4.34668L7.42555 1.11914C7.56525 0.968726 7.75862 0.880284 7.96266 0.874999C8.16653 0.869804 8.36355 0.947901 8.51051 1.09082C8.58326 1.16161 8.64179 1.24633 8.68238 1.33984C8.72298 1.43339 8.74529 1.53457 8.74781 1.63672Z" fill="black"/>
</svg>`

type RollingContentWrapperProps = {
  value?: {
    middleTexts: {
      type: 'NORMAL' | 'WITH_DOWN_CARAT' | 'NORMAL_BOLD'
      text: string
    }[]
    iconSVG?: string
    iconUrl?: string
    iconClassName?: string
  }
  className?: string
}

const RollingContentWrapper = ({ value, className = '' }: RollingContentWrapperProps) => {
  if (!value) {
    return null
  }

  const { middleTexts, iconSVG, iconUrl, iconClassName } = value

  return (
    <div className={`flex items-center w-full pl-3 pr-4 py-3 ${className}`}>
      {iconSVG || iconUrl ? (
        <div className='flex items-center mr-3 flex-shrink-0'>
          {iconSVG ? <RenderHTML innerHTMLText={iconSVG} /> : <img src={iconUrl} alt='icon' className={`w-8 h-8 ${iconClassName ?? ''}`} />}
        </div>
      ) : null}
      <div className='flex flex-col w-1 flex-1 min-w-0'>
        {middleTexts.map((middleText, index) => {
          if (!middleText.text) {
            return null
          }
          return middleText.type === 'NORMAL' || middleText.type === 'NORMAL_BOLD' ? (
            <NormalMiddleText key={`${index}`} text={middleText.text} isBold={middleText.type === 'NORMAL_BOLD'} />
          ) : (
            <WithDownCaratMiddleText key={`${index}`} text={middleText.text} />
          )
        })}
      </div>
    </div>
  )
}

const NormalMiddleText = ({ text, isBold }: { text: string, isBold: boolean }) => {
  return <div className={`text-[13px] truncate max-w-[90%] 
    ${isBold ? 'text-[#232323] font-[500]' : 'text-[#6A6E6E] font-[400]'}`}>{text}</div>
}

const WithDownCaratMiddleText = ({ text }: { text: string }) => {
  return (
    <div className='flex items-center gap-1 w-full'>
      <span className='text-[#232323] font-[500] text-[13px] whitespace-nowrap overflow-hidden text-ellipsis max-w-full '>
        {text}
      </span>
      <div className='pt-[1px]'>
        <RenderHTML innerHTMLText={downCaratIcon} />
      </div>
    </div>
  )
}
